import type { Condition } from '@repo/api-gw-sdk';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';
import { parseJson } from '@/utils/string';

const paramTypes: Record<string, string> = {
  pageIndex: 'number',
  pageSize: 'number',
  query: 'condition',
  startDate: 'date',
  endDate: 'date',
};

// pageIndex/pageSize are the only numeric params whose out-of-range values would
// break the table (negative page, zero-size page); clamp them at the boundary.
const clampNumber = (key: string, value: number) => {
  if (key === 'pageIndex') {
    return Math.max(0, value);
  }
  if (key === 'pageSize') {
    return Math.max(1, value);
  }
  return value;
};

export const deserializeSearchParams = (searchParams: URLSearchParams) => {
  return [...searchParams.entries()].reduce<Record<string, unknown>>(
    (agg, [key, value]) => {
      switch (paramTypes[key]) {
        case 'number': {
          // parseInt tolerates trailing garbage ("12abc" -> 12); require a clean integer.
          if (!/^-?\d+$/.test(value)) {
            break;
          }
          const parsed = parseInt(value, 10);
          if (!isNaN(parsed)) {
            agg[key] = clampNumber(key, parsed);
          }
          break;
        }
        case 'boolean': {
          if (value === 'true') {
            agg[key] = true;
          } else if (value === 'false') {
            agg[key] = false;
          }
          break;
        }
        case 'condition': {
          if (value) {
            const parsed = parseJson(value);
            if (parsed) {
              agg[key] = parsed;
            }
          }
          break;
        }
        case 'date': {
          if (value) {
            const parsed = new Date(value);
            // Reject malformed dates so serialize()'s toISOString() can never throw.
            if (!isNaN(parsed.getTime())) {
              agg[key] = parsed;
            }
          }
          break;
        }
        default: {
          agg[key] = value;
        }
      }

      return agg;
    },
    {}
  );
};

export const serialize = (params: Record<string, unknown>) => {
  return Object.entries(params).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (key === 'query') {
        if (value) {
          acc[key] = toJsonText(value as Condition);
        }
      } else if (value instanceof Date) {
        if (!isNaN(value.getTime())) {
          acc[key] = value.toISOString();
        }
      } else if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        acc[key] = String(value);
      }
      return acc;
    },
    {}
  );
};
